import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import PortalLayout from '../components/PortalLayout';
import { useTheme } from '../context/ThemeContext';
import './RiskAnalysis.css'; // Reuse the same styles

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

// Module-specific config for HaliContract
const moduleConfig = {
  name: 'HaliContract',
  title: 'Contract Analyzer Agent',
  description: 'AI-powered contract analysis and clause extraction',
  icon: '📜',
  color: '#10b981', // Emerald green
  iframePath: '/halilab/contract-analyzer',
  moduleId: 'contract-analyzer',
  quotaUnit: 'analyses',
  warningThreshold: 80,
  criticalThreshold: 95
};

const ContractAnalyzer = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);

  // Theme-aware colors
  const colors = {
    cardBg: isDark ? '#1e293b' : 'white',
    textPrimary: isDark ? '#f3f4f6' : '#1f2937',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    border: isDark ? '#334155' : '#e5e7eb',
    infoBg: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
    infoText: isDark ? '#6ee7b7' : '#047857'
  };

  const loadUserData = useCallback(async () => {
    try {
      // Check URL parameters first (passed from main platform)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      const urlToken = urlParams.get('token');

      // If auth comes from URL, store it for future use
      if (urlEmail) {
        localStorage.setItem('halisoft_email', urlEmail);
        console.log('[ContractAnalyzer] Auth received from URL:', urlEmail);
      }
      if (urlToken) {
        localStorage.setItem('halisoft_token', urlToken);
      }

      // Check if user is authenticated - check localStorage
      const token = localStorage.getItem('halisoft_token');
      const storedUser = localStorage.getItem('halisoft_user');
      const storedEmail = localStorage.getItem('halisoft_email');

      // Use URL email first, then localStorage
      let email = urlEmail || storedEmail;
      if (!email && storedUser) {
        try {
          email = JSON.parse(storedUser)?.email;
        } catch (e) { /* ignore */ }
      }

      // If no authentication at all, redirect to login
      if (!token && !storedUser && !storedEmail && !urlEmail) {
        console.log('[ContractAnalyzer] No authentication found, redirecting to login');
        navigate('/login?redirect=/contract-analyzer');
        return;
      }

      // Try to get user from API
      const userRes = await api.get('/api/user/me').catch((err) => {
        // If 401 but we have email from URL, continue with that
        if (err.response?.status === 401 && email) {
          return { data: { email } };
        }
        if (err.response?.status === 401) {
          throw err;
        }
        // Otherwise use stored email
        return { data: { email: email || 'Guest' } };
      });

      const userEmail = userRes.data?.email || email;
      if (!userEmail || userEmail === 'Guest') {
        console.log('[ContractAnalyzer] No valid email, redirecting to login');
        navigate('/login?redirect=/contract-analyzer');
        return;
      }

      // Module-specific: ContractAnalyzer has its own plan/quota
      const [quotaRes, subRes] = await Promise.all([
        api.get(`/api/user/quota?email=${encodeURIComponent(userEmail)}&module=${encodeURIComponent(moduleConfig.moduleId)}`),
        api.get(`/api/subscriptions/current?email=${encodeURIComponent(userEmail)}&module=${encodeURIComponent(moduleConfig.moduleId)}`).catch(() => ({ data: null }))
      ]);

      setUser(userRes.data);
      setSubscription(subRes.data);
      setQuota(quotaRes.data);

      // Show alert if quota is low
      if (quotaRes.data.percentageUsed >= moduleConfig.warningThreshold) {
        setShowQuotaAlert(true);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) {
        // Clear stale data and redirect to login
        localStorage.removeItem('halisoft_token');
        localStorage.removeItem('halisoft_user');
        localStorage.removeItem('halisoft_email');
        navigate('/login?redirect=/contract-analyzer');
        return;
      }
      // For other errors, show error state
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  if (loading) {
    return (
      <PortalLayout>
        <div className="risk-analysis-loading">
          <div className="spinner-large"></div>
          <p>Loading...</p>
        </div>
      </PortalLayout>
    );
  }

  const quotaPercentage = quota ? (quota.used / quota.limit) * 100 : 0;
  const isQuotaLow = quotaPercentage >= moduleConfig.warningThreshold;
  const isQuotaCritical = quotaPercentage >= moduleConfig.criticalThreshold;
  const mainPlatformUrl = getMainPlatformUrl();

  return (
    <PortalLayout>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }}>
        {/* Compact Info Bar with Module Header */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'stretch'
        }}>
          {/* Module Info */}
          <div style={{
            flex: 2,
            minWidth: '280px',
            background: isDark
              ? `linear-gradient(135deg, ${moduleConfig.color}25, ${moduleConfig.color}10)`
              : `linear-gradient(135deg, ${moduleConfig.color}15, ${moduleConfig.color}05)`,
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            border: isDark ? `1px solid ${moduleConfig.color}40` : `1px solid ${moduleConfig.color}20`,
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}cc)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              boxShadow: `0 4px 12px ${moduleConfig.color}40`
            }}>
              {moduleConfig.icon}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: colors.textPrimary }}>
                {moduleConfig.name}
              </h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: moduleConfig.color, fontWeight: '600' }}>
                {moduleConfig.title}
              </p>
            </div>
          </div>

          {/* Quota Card */}
          <div style={{
            flex: 1,
            minWidth: '180px',
            background: colors.infoBg,
            borderRadius: '12px',
            padding: '0.75rem 1rem',
            border: isDark ? `1px solid ${moduleConfig.color}30` : `1px solid ${moduleConfig.color}20`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <span style={{ fontSize: '0.75rem', color: colors.infoText, fontWeight: '600' }}>Usage</span>
              <span style={{ fontSize: '0.85rem', fontWeight: '700', color: colors.infoText }}>
                {quota?.used || 0} / {quota?.limit || 5}
              </span>
            </div>
            <div style={{ height: '6px', background: isDark ? 'rgba(16,185,129,0.2)' : '#a7f3d0', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(quotaPercentage, 100)}%`,
                height: '100%',
                background: isQuotaCritical ? '#dc2626' : isQuotaLow ? '#f59e0b' : moduleConfig.color,
                borderRadius: '3px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <p style={{ margin: '0.3rem 0 0 0', fontSize: '0.7rem', color: colors.textSecondary }}>
              {quota?.remaining || 0} {moduleConfig.quotaUnit} remaining
            </p>
          </div>

          {/* Plan Card */}
          <div style={{
            flex: 1,
            minWidth: '160px',
            background: colors.cardBg,
            borderRadius: '12px',
            padding: '0.75rem 1rem',
            border: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.7rem', color: colors.textSecondary }}>Current Plan</p>
                <p style={{ margin: '0.15rem 0 0 0', fontSize: '1rem', fontWeight: '700', color: moduleConfig.color }}>
                  {subscription?.name || quota?.plan || 'FREE'}
                </p>
              </div>
              <button
                onClick={() => {
                  const email = user?.email || localStorage.getItem('halisoft_email') || '';
                  navigate(`/subscription?module=${moduleConfig.moduleId}${email ? `&email=${encodeURIComponent(email)}` : ''}#pricing`);
                }}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Upgrade
              </button>
            </div>
          </div>
        </div>

        {/* Quota Alert Banner */}
        {showQuotaAlert && quota && quota.remaining > 0 && (
          <div style={{
            background: isQuotaCritical
              ? (isDark ? 'rgba(220,38,38,0.2)' : '#fef2f2')
              : (isDark ? 'rgba(245,158,11,0.2)' : '#fffbeb'),
            border: `1px solid ${isQuotaCritical ? '#fca5a5' : '#fcd34d'}`,
            borderRadius: '10px',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.1rem' }}>{isQuotaCritical ? '🚨' : '⚠️'}</span>
            <span style={{ flex: 1, fontSize: '0.85rem', color: isQuotaCritical ? '#dc2626' : '#d97706' }}>
              {isQuotaCritical
                ? `Warning! Only ${quota.remaining} ${moduleConfig.quotaUnit} left this month.`
                : `Low quota: ${quota.remaining} ${moduleConfig.quotaUnit} remaining.`}
            </span>
            <button
              onClick={() => setShowQuotaAlert(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.textSecondary,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Quota Exceeded Banner */}
        {quota && quota.remaining === 0 && (
          <div style={{
            background: isDark ? 'rgba(220,38,38,0.2)' : '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '10px',
            padding: '1rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>🚫</span>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: 0, color: '#dc2626' }}>Quota Exhausted</h4>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: colors.textSecondary }}>
                You have used all {quota.limit} {moduleConfig.quotaUnit} this month. Upgrade to continue.
              </p>
            </div>
            <button
              onClick={() => {
                const email = user?.email || localStorage.getItem('halisoft_email') || '';
                navigate(`/subscription?module=${moduleConfig.moduleId}${email ? `&email=${encodeURIComponent(email)}` : ''}#pricing`);
              }}
              style={{
                padding: '0.6rem 1.25rem',
                background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              🚀 View Plans
            </button>
          </div>
        )}

        {/* Main Content - iFrame to Contract Analyzer Service */}
        <div style={{
          flex: 1,
          minHeight: '500px',
          background: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          {quota && quota.remaining > 0 ? (
            <iframe
              src={`${mainPlatformUrl}${moduleConfig.iframePath}?email=${encodeURIComponent(user?.email || localStorage.getItem('halisoft_email') || '')}`}
              title={moduleConfig.name}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '500px'
              }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              allow="clipboard-write"
            />
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
              textAlign: 'center',
              height: '100%'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
              <h2 style={{ margin: '0 0 0.5rem 0', color: colors.textPrimary }}>Service Blocked</h2>
              <p style={{ margin: '0 0 1rem 0', color: colors.textSecondary }}>
                You have exhausted your monthly quota. Upgrade your plan to continue using {moduleConfig.name}.
              </p>
              <p style={{ margin: '0 0 1.5rem 0', fontSize: '0.85rem', color: colors.textSecondary }}>
                Your quota will reset on the 1st of next month.
              </p>
              <button
                onClick={() => {
                  const email = user?.email || localStorage.getItem('halisoft_email') || '';
                  navigate(`/subscription?module=${moduleConfig.moduleId}${email ? `&email=${encodeURIComponent(email)}` : ''}#pricing`);
                }}
                style={{
                  padding: '0.85rem 2rem',
                  background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                🚀 Upgrade Now
              </button>
            </div>
          )}
        </div>

        {/* Floating Chat Badge - Opens HaliGuide on main platform */}
        <button
          onClick={() => {
            const width = 420;
            const height = 700;
            const left = window.screenX + 50;
            const top = window.screenY + 100;
            window.open(
              `${mainPlatformUrl}/landing`,
              'HaliGuide',
              `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
            );
          }}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1500,
            background: `linear-gradient(135deg, ${moduleConfig.color}90, ${moduleConfig.color})`,
            border: 'none',
            padding: '14px 20px',
            borderRadius: '50px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: `0 8px 32px ${moduleConfig.color}60`,
            transition: 'all 0.3s ease',
            color: 'white',
            fontWeight: '600',
            fontSize: '0.95rem'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = `0 12px 40px ${moduleConfig.color}70`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 8px 32px ${moduleConfig.color}60`;
          }}
          title="Chat with HaliGuide"
        >
          <span style={{ fontSize: '1.2rem' }}>💬</span>
          <span>Need Help?</span>
        </button>
      </div>
    </PortalLayout>
  );
};

export default ContractAnalyzer;
